import {ClassNames} from '../Interfaces/class-names';
import SearchAlternativeRow from './SearchAlternativeRow';
import templates from '../Helpers/templates';
import {wrap} from '../Helpers/wrap';
import {DomainResponse, DomainSuggestions} from '../Helpers/Fetch';
import {Locale} from '../Interfaces/locale';
import Cart from './Cart';

export default class SearchAlternatives {
    public readonly element: HTMLRsHeaderElement;
    private readonly classNames: ClassNames;
    private readonly locale: Locale;
    private readonly rowType: 'preferred' | 'alternative';
    private readonly cart: Cart;
    private abortController: AbortController | null = null;

    public constructor({
                           element,
                           classNames,
                           locale,
                           rowType = 'alternative',
                           cart,
                       }: {
        element: HTMLRsHeaderElement,
        classNames: ClassNames,
        locale: Locale,
        rowType: 'preferred' | 'alternative',
        cart: Cart,
    }) {
        this.element = element;
        this.classNames = classNames;
        this.locale = locale;
        this.rowType = rowType;
        this.cart = cart;
    }

    /**
     * Renders one row per suffix. When `knownDetails` is supplied (the searched
     * domain's own response) the row renders immediately instead of re-fetching.
     */
    public showDomains(suggestions: DomainSuggestions, knownDetails?: DomainResponse): this {
        if (suggestions === undefined) {
            return;
        }
        const suffixes = suggestions.suffixes ?? [];
        const uniqueSuffixes = Array.from(new Set(suffixes));
        this.removeAllRows();
        if (uniqueSuffixes.length === 0) {
            return;
        }
        this.element.style.display = 'block';

        this.element.classList.add(this.classNames.searchAlternativeContainerActive);
        this.abortController = new AbortController();
        uniqueSuffixes.forEach(alternativeDomain => {
            const row = new SearchAlternativeRow({
                element: this.getTemplate('searchAlternativeRow', this.rowType, suggestions.prefix + alternativeDomain),
                prefix: suggestions.prefix,
                suffix: alternativeDomain,
                classNames: this.classNames,
                locale: this.locale,
                cart: this.cart,
                knownDetails,
                signal: this.abortController.signal,
            });
            wrap(row.element, this.element);
        });
        return this;
    }

    /** Cancels any in-flight row lookups from the previous search. */
    public abortPending(): void {
        this.abortController?.abort();
        this.abortController = null;
    }

    private removeAllRows(): void {
        this.abortPending();
        this.element.querySelectorAll(`.${this.classNames.searchAlternativeRow}`).forEach(row => {
            row.remove();
        });
    }

    private getTemplate(template: keyof typeof templates, ...args: any): any {
        return templates[template].call(this, {classNames: this.classNames}, ...args);
    }
}
